<template>
    <Transition name="fade">
        <div
            v-if="userMessageCount <= MAX_MESSAGES"
            role="status"
            class="activation-pill bg-white rounded-full border-2 border-border px-6 py-3 shadow-md transition-opacity min-w-72"
            :class="{ 'opacity-0 delay-2500': userMessageCount === MAX_MESSAGES }">
            <div class="details flex justify-between gap-4 mb-1">
                <Transition name="slide" mode="out-in">
                    <span :key="currentMessageShown">{{ currentMessageShown }}</span>
                </Transition>
                <span>{{ userMessageCount }}/{{ MAX_MESSAGES }}</span>
            </div>

            <BaseProgressBar :value="percentComplete" size="sm" :aria-label="`Chat activation: ${userMessageCount} of ${MAX_MESSAGES} messages`" />
        </div>
    </Transition>
</template>

<script setup lang="ts">
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import type { ChatMessage } from "~vue/types/chat";
import { computed } from "vue";

type ChatActivationProps = {
    messages: ChatMessage[];
};

const { messages = [] } = defineProps<ChatActivationProps>();

const MESSAGES = ["I'm here to listen", "This is helpful to hear", "Interesting, tell me more", "Trying to understand the root cause", "Forming my thoughts", "Ok, I'm ready"];

const MAX_MESSAGES = 5;

const userMessageCount = computed(() => messages.filter((m) => m.role === "user" && !m.hidden).length);
const currentMessageShown = computed(() => MESSAGES[userMessageCount.value]);
const percentComplete = computed(() => (userMessageCount.value / MAX_MESSAGES) * 100);
</script>

<style scoped>
/* The prototype thread carries no activation pill, so it only renders on the
   legacy theme. */
html[data-theme="tonal"] .activation-pill {
    display: none;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 200ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.gradient-bg {
    background: linear-gradient(125deg, #ff2891 -22.98%, #003fdc 113.56%);
}

.slide-leave-active,
.slide-enter-active {
    transition: all 300ms ease-out;
}

.slide-leave-from,
.slide-enter-to {
    opacity: 1;
    transform: translateY(0);
}

.slide-enter-from {
    opacity: 0;
    transform: translateY(-1lh);
}
.slide-leave-to {
    opacity: 0;
    transform: translateY(1lh);
}
</style>
